// src/components/Properties/StrategicShareModal.jsx
// ======================================================
// 🔥 STRATEGIC SHARE MODAL ULTRA PREMIUM — NIVEL SUPERIOR A AIRBNB
// V2 ENTERPRISE PORTAL
// ======================================================
// Compartir con mensajes pre-cargados ultra estratégicos por audiencia:
// - 💑 Parceiro(a): mensaje romántico/convivencia
// - 👨‍👩‍👧‍👦 Família: mensaje de oportunidad grupal
// - 👯 Amigos: mensaje casual/entusiasmo
// - 💼 Investidor: mensaje de negocio/ROI
// - 📋 Copiar link con animación
// - 📧 Email pre-formateado
// - 🔲 QR Code generado al instante
// 🔥 V2: Portal a document.body · Dark mode automático
// ======================================================

import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Heart, Users, UserPlus, Briefcase, Copy, Check,
  Mail, QrCode, Share2, Sparkles, ChevronRight, Zap,
  MessageCircle, ArrowRight, Star, Home, MapPin
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { usePortal, useBodyScrollLock, useEscapeKey, portalize } from '../../utils/portalUtils';

// ======================================================
// 🔧 GENERADOR DE QR CODE (PURO)
// ======================================================
const generateQRCode = (text, size = 200) => {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(text)}&margin=10`;
    img.onload = () => {
      ctx.drawImage(img, 0, 0, size, size);
      resolve(canvas.toDataURL());
    };
    img.onerror = () => resolve(null);
  });
};

// ======================================================
// 🎯 MENSAJES ESTRATÉGICOS POR AUDIENCIA
// ======================================================

const SHARE_STRATEGIES = [
  {
    id: 'partner',
    icon: Heart,
    color: 'from-rose-400 to-pink-500',
    bgLight: 'bg-rose-50 dark:bg-rose-950/30',
    textColor: 'text-rose-600 dark:text-rose-400',
    borderColor: 'border-rose-200 dark:border-rose-800',
    hoverBorder: 'hover:border-rose-300 dark:hover:border-rose-700',
    label: 'Para meu Parceiro(a)',
    emoji: '💑',
    getMessage: (property) => {
      const title = property?.title || 'esse imóvel';
      const price = property?.price
        ? `R$ ${Number(property.price).toLocaleString('pt-BR')}`
        : 'valor a consultar';
      return `*Amor, olha isso!* 💑\n\n` +
        `Vi ${title} e lembrei na hora do que conversamos sobre nosso futuro lar. 🏡\n\n` +
        `💰 *Valor:* ${price}\n` +
        `📍 *Local:* ${property?.address || property?.neighborhood || 'a ver'}\n\n` +
        `Dá uma olhada e me diz o que você acha... será que é a nossa chance? ✨`;
    },
  },
  {
    id: 'family',
    icon: Users,
    color: 'from-blue-400 to-cyan-500',
    bgLight: 'bg-blue-50 dark:bg-blue-950/30',
    textColor: 'text-blue-600 dark:text-blue-400',
    borderColor: 'border-blue-200 dark:border-blue-800',
    hoverBorder: 'hover:border-blue-300 dark:hover:border-blue-700',
    label: 'Para a Família',
    emoji: '👨‍👩‍👧‍👦',
    getMessage: (property) => {
      const title = property?.title || 'esse imóvel';
      const price = property?.price
        ? `R$ ${Number(property.price).toLocaleString('pt-BR')}`
        : 'valor a consultar';
      const area = property?.area ? `${property.area}m²` : '';
      return `*Família, olhem isso!* 👨‍👩‍👧‍👦\n\n` +
        `Achei uma oportunidade incrível pra gente: ${title}.\n\n` +
        `💰 *Valor:* ${price}\n` +
        (area ? `📐 *Área:* ${area}\n` : '') +
        `📍 *Local:* ${property?.address || property?.neighborhood || 'a ver'}\n\n` +
        `O que vocês acham? Podemos marcar uma visita juntos! 🙏`;
    },
  },
  {
    id: 'friends',
    icon: UserPlus,
    color: 'from-amber-400 to-orange-500',
    bgLight: 'bg-amber-50 dark:bg-amber-950/30',
    textColor: 'text-amber-600 dark:text-amber-400',
    borderColor: 'border-amber-200 dark:border-amber-800',
    hoverBorder: 'hover:border-amber-300 dark:hover:border-amber-700',
    label: 'Para Amigos',
    emoji: '👯',
    getMessage: (property) => {
      const title = property?.title || 'esse imóvel';
      const price = property?.price
        ? `R$ ${Number(property.price).toLocaleString('pt-BR')}`
        : 'valor a consultar';
      const quartos = property?.bedrooms
        ? `${property.bedrooms} quarto${property.bedrooms > 1 ? 's' : ''}`
        : '';
      return `*Gente, olha isso!* 👯\n\n` +
        `Encontrei ${title} e tive que compartilhar com vocês!\n\n` +
        `💰 *Valor:* ${price}\n` +
        (quartos ? `🛏️ *${quartos}*\n` : '') +
        `📍 *Local:* ${property?.address || property?.neighborhood || 'a ver'}\n\n` +
        `O que vocês acham? Bora marcar uma visita pra ver de perto? 👀`;
    },
  },
  {
    id: 'investor',
    icon: Briefcase,
    color: 'from-emerald-400 to-green-500',
    bgLight: 'bg-emerald-50 dark:bg-emerald-950/30',
    textColor: 'text-emerald-600 dark:text-emerald-400',
    borderColor: 'border-emerald-200 dark:border-emerald-800',
    hoverBorder: 'hover:border-emerald-300 dark:hover:border-emerald-700',
    label: 'Para um Investidor',
    emoji: '💼',
    getMessage: (property) => {
      const title = property?.title || 'este imóvel';
      const price = property?.price
        ? `R$ ${Number(property.price).toLocaleString('pt-BR')}`
        : 'valor a consultar';
      const area = property?.area ? `${property.area}m²` : '';
      return `*Oportunidade de investimento!* 💼\n\n` +
        `Analisei ${title} e o potencial de valorização é excelente. 📊\n\n` +
        `💰 *Valor:* ${price}\n` +
        (area ? `📐 *Área:* ${area}\n` : '') +
        `📍 *Localização:* ${property?.address || property?.neighborhood || 'a ver'}\n\n` +
        `Vale a pena avaliar. Me diz o que você acha! 💪`;
    },
  },
];

// ======================================================
// 🎨 STRATEGIC SHARE MODAL COMPONENT
// ======================================================

export default function StrategicShareModal({
  isOpen,
  onClose,
  property,
  url,
  primaryColor = '#2563eb',
}) {
  const { isDark } = useTheme();
  const mounted = usePortal();

  // ✅ Portal hooks
  useBodyScrollLock(isOpen);
  useEscapeKey(isOpen, onClose);

  const [copied, setCopied] = useState(false);
  const [qrCode, setQrCode] = useState(null);
  const [showQR, setShowQR] = useState(false);
  const [selectedStrategy, setSelectedStrategy] = useState(null);
  const [copyLabel, setCopyLabel] = useState('Copiar Link');
  const qrGeneratedRef = useRef(false);

  useEffect(() => {
    if (isOpen && !qrGeneratedRef.current && url) {
      qrGeneratedRef.current = true;
      generateQRCode(url).then(setQrCode);
    }
    if (!isOpen) {
      qrGeneratedRef.current = false;
      setQrCode(null);
      setShowQR(false);
      setSelectedStrategy(null);
    }
  }, [isOpen, url]);

  const getWhatsAppUrl = useCallback((strategyId) => {
    const strategy = SHARE_STRATEGIES.find(s => s.id === strategyId);
    if (!strategy) return '';
    const message = strategy.getMessage(property);
    const fullMessage = encodeURIComponent(message + `\n\n🔗 ${url}`);
    return `https://wa.me/?text=${fullMessage}`;
  }, [property, url]);

  const getEmailUrl = useCallback(() => {
    const subject = encodeURIComponent(`🏡 Imóvel: ${property?.title || 'Imóvel'}`);
    const body = encodeURIComponent(
      `Olá!\n\n` +
      `Vi este imóvel e fiquei interessado:\n\n` +
      `🏡 ${property?.title || 'Imóvel'}\n` +
      `💰 ${property?.price ? `R$ ${Number(property.price).toLocaleString('pt-BR')}` : 'Valor sob consulta'}\n` +
      `📍 ${property?.address || property?.neighborhood || ''}\n\n` +
      `🔗 ${url}\n\n` +
      `Poderia me passar mais informações?`
    );
    return `mailto:?subject=${subject}&body=${body}`;
  }, [property, url]);

  const handleCopyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setCopyLabel('Link Copiado! ✅');
      setTimeout(() => {
        setCopied(false);
        setCopyLabel('Copiar Link');
      }, 2000);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = url;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setCopyLabel('Link Copiado! ✅');
      setTimeout(() => {
        setCopied(false);
        setCopyLabel('Copiar Link');
      }, 2000);
    }
  }, [url]);

  const handleStrategyClick = useCallback((strategyId) => {
    setSelectedStrategy(strategyId);
    setTimeout(() => {
      window.open(getWhatsAppUrl(strategyId), '_blank');
      setSelectedStrategy(null);
    }, 300);
  }, [getWhatsAppUrl]);

  if (!isOpen) return null;

  // 🌓 Clases dark mode
  const bgModal = "bg-white dark:bg-slate-900";
  const bgInfoBar = "bg-white/80 dark:bg-slate-800/80";
  const textDark = "text-slate-800 dark:text-slate-100";
  const textGray = "text-slate-500 dark:text-slate-400";
  const textGrayStrong = "text-slate-700 dark:text-slate-300";
  const textXLight = "text-slate-400 dark:text-slate-500";
  const bgBtnDefault = "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700";
  const bgBtnQR = "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700";
  const bgBtnQRActive = "bg-slate-800 dark:bg-slate-700 text-white";
  const bgQR = "bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-600";
  const bgPulse = "bg-slate-100 dark:bg-slate-700";
  const btnClose = "hover:bg-white/80 dark:hover:bg-slate-700/80";
  const borderDefault = "border-slate-100 dark:border-slate-700";
  const textFooter = "text-slate-400 dark:text-slate-500";

  const modalContent = (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="fixed inset-0 z-[99999] flex items-center justify-center p-4"
        onClick={onClose}
      >
        {/* Backdrop con blur */}
        <div className="absolute inset-0 bg-black/60 dark:bg-black/80 backdrop-blur-sm" />

        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ type: 'spring', stiffness: 400, damping: 30 }}
          onClick={(e) => e.stopPropagation()}
          className={`relative w-full max-w-md ${bgModal} rounded-3xl shadow-2xl overflow-hidden`}
        >
          {/* Header con gradiente */}
          <div
            className="relative p-6 pb-4"
            style={{
              background: `linear-gradient(135deg, ${primaryColor}08 0%, ${primaryColor}15 100%)`,
            }}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-3">
                <div
                  className="w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg"
                  style={{
                    background: `linear-gradient(135deg, ${primaryColor}, ${primaryColor}dd)`,
                  }}
                >
                  <Share2 className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className={`text-xl font-bold ${textDark} flex items-center gap-1`}>
                    Compartilhar
                    <Sparkles className="w-4 h-4 text-amber-500" />
                  </h2>
                  <p className={`text-xs ${textGray} mt-0.5`}>
                    Escolha com quem compartilhar
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className={`p-2 rounded-full transition-colors shadow-sm ${btnClose}`}
              >
                <X className={`w-5 h-5 ${textGray}`} />
              </button>
            </div>

            {/* Info rápida del inmueble */}
            <div className={`flex items-center gap-2 mt-3 px-3 py-2 ${bgInfoBar} rounded-xl text-sm`}>
              <Home className="w-4 h-4 text-slate-400 dark:text-slate-500 flex-shrink-0" />
              <span className={`font-medium truncate ${textGrayStrong}`}>
                {property?.title || 'Imóvel'}
              </span>
              {property?.price && (
                <span className={`text-xs ${textGray} flex-shrink-0`}>
                  {`R$ ${Number(property.price).toLocaleString('pt-BR')}`}
                </span>
              )}
            </div>
          </div>

          {/* Lista de estrategias */}
          <div className="px-4 py-3 space-y-2">
            <p className={`text-xs font-semibold ${textXLight} uppercase tracking-wider px-2 mb-1`}>
              Mensagens Estratégicas
            </p>

            {SHARE_STRATEGIES.map((strategy, index) => {
              const Icon = strategy.icon;
              const isSelected = selectedStrategy === strategy.id;

              return (
                <motion.button
                  key={strategy.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05, type: 'spring', stiffness: 300 }}
                  whileHover={{ scale: 1.01, x: 4 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleStrategyClick(strategy.id)}
                  className={`w-full flex items-center gap-4 p-4 rounded-2xl border-2 transition-all text-left group ${
                    isSelected
                      ? `${strategy.borderColor} ${strategy.bgLight} scale-[0.98]`
                      : `${borderDefault} ${strategy.hoverBorder} hover:shadow-md ${bgModal}`
                  }`}
                >
                  <div
                    className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 bg-gradient-to-br ${strategy.color} shadow-md group-hover:shadow-lg transition-all`}
                  >
                    <Icon className="w-6 h-6 text-white" />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{strategy.emoji}</span>
                      <span className={`font-semibold text-sm ${strategy.textColor}`}>
                        {strategy.label}
                      </span>
                    </div>
                    <p className={`text-xs ${textGray} mt-0.5 truncate`}>
                      {strategy.getMessage(property).replace(/\*/g, '').substring(0, 60)}...
                    </p>
                  </div>

                  <div className="flex-shrink-0 flex items-center gap-1">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                      isSelected ? 'bg-green-500' : 'bg-slate-100 dark:bg-slate-800 group-hover:bg-green-100 dark:group-hover:bg-green-900/40'
                    }`}>
                      {isSelected ? (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                        >
                          <Check className="w-4 h-4 text-white" />
                        </motion.div>
                      ) : (
                        <MessageCircle className={`w-4 h-4 ${strategy.textColor}`} />
                      )}
                    </div>
                  </div>
                </motion.button>
              );
            })}
          </div>

          {/* Opciones rápidas */}
          <div className="px-4 pb-2">
            <p className={`text-xs font-semibold ${textXLight} uppercase tracking-wider px-2 mb-2 mt-2`}>
              Mais Opções
            </p>

            <div className="flex gap-2">
              {/* Copiar Link */}
              <motion.button
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleCopyLink}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                  copied
                    ? 'bg-emerald-500 text-white shadow-lg'
                    : bgBtnDefault
                }`}
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                <span className="hidden sm:inline">{copyLabel}</span>
              </motion.button>

              {/* Email */}
              <motion.a
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.95 }}
                href={getEmailUrl()}
                target="_blank"
                rel="noreferrer"
                className={`flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-medium transition-all ${bgBtnDefault}`}
              >
                <Mail className="w-4 h-4" />
                <span className="hidden sm:inline">Email</span>
              </motion.a>

              {/* QR Code */}
              <motion.button
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowQR(!showQR)}
                className={`flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                  showQR ? bgBtnQRActive : bgBtnQR
                }`}
              >
                <QrCode className="w-4 h-4" />
                <span className="hidden sm:inline">QR</span>
              </motion.button>
            </div>
          </div>

          {/* QR Code expandido */}
          <AnimatePresence>
            {showQR && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="overflow-hidden"
              >
                <div className="px-4 pb-6 flex flex-col items-center">
                  <div className={`${bgQR} border-2 rounded-2xl p-3 shadow-inner mt-3`}>
                    {qrCode ? (
                      <img
                        src={qrCode}
                        alt="QR Code"
                        className="w-36 h-36 rounded-xl"
                      />
                    ) : (
                      <div className={`w-36 h-36 ${bgPulse} rounded-xl flex items-center justify-center animate-pulse`}>
                        <QrCode className="w-8 h-8 text-slate-300 dark:text-slate-600" />
                      </div>
                    )}
                  </div>
                  <p className={`text-xs ${textGray} mt-2 text-center`}>
                    Aponte a câmera do celular 📱
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Footer sutil */}
          <div className="px-4 pb-4 pt-2">
            <div className="flex items-center justify-center gap-2">
              <Zap className="w-3 h-3 text-amber-500" />
              <p className={`text-[10px] ${textFooter}`}>
                Mensagens otimizadas para conversão • Abre no WhatsApp
              </p>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );

  return portalize(mounted, modalContent);
}